import { useEffect, useMemo, useState } from 'react';
import { Edit3, KeyRound, LoaderCircle, Plus, Search, ShieldCheck, Trash2, UserCog } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { ConfirmDialog, EmptyState, Modal, Notice, PageLoader, StatusBadge, formatTimestamp } from '../components/UI';
import { ACTION_KEYS, MODULES, PUSH_SUPPORTED_MODULES, VILLAGE_MODULE, emptyPermissions } from '../config/modules';
import { useAuth } from '../context/AuthContext';
import {
  createAdminAccount,
  removeAdminAccount,
  resetAdminPassword,
  setAnnouncementAdminPermissions,
  updateAdminAccount,
} from '../services/adminApi';
import { subscribeToCollection } from '../services/data';

const PERMISSION_MODULES = {
  village: VILLAGE_MODULE.label,
  ...Object.fromEntries(Object.entries(MODULES).map(([key, value]) => [key, value.label])),
  auditLogs: 'Audit Logs',
};

const DEFAULT_FORM = {
  name: '', email: '', password: '', active: true, permissions: emptyPermissions(),
};

function actionAllowed(moduleKey, action) {
  if (moduleKey === 'auditLogs') return action === 'view';
  if (action === 'notify') return PUSH_SUPPORTED_MODULES.includes(moduleKey);
  return true;
}

function PermissionMatrix({ value, onChange }) {
  const setPermission = (moduleKey, action, checked) => onChange({
    ...value,
    [moduleKey]: { ...(value[moduleKey] || {}), [action]: checked },
  });

  const toggleModule = (moduleKey, checked) => onChange({
    ...value,
    [moduleKey]: Object.fromEntries(
      ACTION_KEYS.map((action) => [action, actionAllowed(moduleKey, action) ? checked : false]),
    ),
  });

  return (
    <div className="permission-wrap">
      <div className="permission-table-scroll">
        <table className="permission-table">
          <thead><tr><th>Module</th><th>All</th>{ACTION_KEYS.map((action) => <th key={action}>{action}</th>)}</tr></thead>
          <tbody>{Object.entries(PERMISSION_MODULES).map(([moduleKey, label]) => {
            const permissions = value?.[moduleKey] || {};
            const validActions = ACTION_KEYS.filter((action) => actionAllowed(moduleKey, action));
            const all = validActions.every((action) => permissions[action]);
            return (
              <tr key={moduleKey}>
                <td><strong>{label}</strong></td>
                <td><input type="checkbox" checked={all} onChange={(event) => toggleModule(moduleKey, event.target.checked)} /></td>
                {ACTION_KEYS.map((action) => {
                  const disabled = !actionAllowed(moduleKey, action);
                  return <td key={action}><input type="checkbox" disabled={disabled} checked={Boolean(permissions[action]) && !disabled} onChange={(event) => setPermission(moduleKey, action, event.target.checked)} /></td>;
                })}
              </tr>
            );
          })}</tbody>
        </table>
      </div>
      <small>View vagar module sidebar ma dekhase nahi. Announcement Notify permission alag chhe, jethi content edit access aapi push broadcast access alag rakhi shakay.</small>
    </div>
  );
}

function AdminForm({ initial, isNew, busy, onSubmit, onCancel }) {
  const [form, setForm] = useState(() => ({ ...DEFAULT_FORM, ...initial, permissions: { ...emptyPermissions(), ...(initial?.permissions || {}) }, password: '' }));
  const [error, setError] = useState('');
  const submit = (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return setError('Name ane email required chhe.');
    if (isNew && form.password.length < 8) return setError('Temporary password minimum 8 characters no hovo joiye.');
    onSubmit(form);
  };

  return (
    <form className="record-form" onSubmit={submit}>
      {error ? <Notice type="error">{error}</Notice> : null}
      <div className="form-grid two">
        <label className="field"><span>Admin name<em>*</em></span><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
        <label className="field"><span>Email address<em>*</em></span><input type="email" disabled={!isNew} value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
      </div>
      {isNew ? <label className="field"><span>Temporary password<em>*</em></span><input type="password" minLength="8" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} /><small>Admin login pachhi password securely share/change karavo.</small></label> : null}
      <label className="toggle-field publish-toggle"><input type="checkbox" checked={form.active} onChange={(event) => setForm({ ...form, active: event.target.checked })} /><span className="toggle-track"><span /></span><span><strong>Active admin account</strong><small>Inactive karta user na tokens revoke thase ane access bandh thase.</small></span></label>
      <fieldset className="list-group"><legend>Module permissions</legend><PermissionMatrix value={form.permissions} onChange={(permissions) => setForm({ ...form, permissions })} /></fieldset>
      <div className="form-actions sticky-actions"><button className="button secondary" type="button" onClick={onCancel} disabled={busy}>Cancel</button><button className="button primary" disabled={busy}>{busy ? <LoaderCircle className="spin" size={17} /> : null}{busy ? 'Saving…' : isNew ? 'Create admin' : 'Update permissions'}</button></div>
    </form>
  );
}

export default function AdminUsersPage() {
  const { isSuperAdmin, can, user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isSuperAdmin && !can('adminUsers', 'view')) return undefined;
    return subscribeToCollection('adminUsers', (records) => { setAdmins(records); setLoading(false); }, (snapshotError) => { setError(snapshotError.message); setLoading(false); });
  }, [can, isSuperAdmin]);

  const filtered = useMemo(() => admins.filter((admin) => `${admin.name} ${admin.email}`.toLowerCase().includes(search.toLowerCase())), [admins, search]);
  if (!isSuperAdmin && !can('adminUsers', 'view')) return <Navigate to="/unauthorized" replace />;

  const saveAdmin = async (form) => {
    setBusy(true); setError('');
    try {
      let targetUid;
      if (creating) {
        const result = await createAdminAccount(form);
        targetUid = result.uid;
      } else {
        targetUid = editing.uid || editing.id;
        await updateAdminAccount({ uid: targetUid, name: form.name, active: form.active, permissions: form.permissions });
      }

      await setAnnouncementAdminPermissions({
        uid: targetUid,
        permissions: form.permissions?.announcements || {},
      });

      setSuccess(creating
        ? `${form.name} mate admin account create thayu.`
        : `${form.name} ni permissions update thai.`);
      setCreating(false); setEditing(null);
    } catch (saveError) { setError(saveError.message || 'Admin save failed.'); }
    finally { setBusy(false); }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    if (newPassword.length < 8) return setError('Password minimum 8 characters no hovo joiye.');
    setBusy(true);
    try { await resetAdminPassword({ uid: resetting.uid || resetting.id, password: newPassword }); setSuccess('Temporary password update thai gayo.'); setResetting(null); setNewPassword(''); }
    catch (resetError) { setError(resetError.message || 'Password reset failed.'); }
    finally { setBusy(false); }
  };

  const removeAdmin = async () => {
    setBusy(true);
    try { await removeAdminAccount({ uid: removing.uid || removing.id }); setSuccess(`${removing.name} no admin account permanently remove thayo.`); setRemoving(null); }
    catch (removeError) { setError(removeError.message || 'Admin removal failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="page-stack">
      <header className="page-heading"><div><p className="eyebrow">Access control</p><h1>Admin Users</h1><p>Super Admin module-wise permissions decide kari shake chhe.</p></div>{isSuperAdmin ? <button className="button primary" onClick={() => setCreating(true)}><Plus size={18} /> Create admin</button> : null}</header>
      {error ? <Notice type="error" onClose={() => setError('')}>{error}</Notice> : null}{success ? <Notice type="success" onClose={() => setSuccess('')}>{success}</Notice> : null}
      <section className="panel-card table-panel">
        <div className="table-toolbar"><label className="search-box"><Search size={18} /><input value={search} placeholder="Search admins…" onChange={(event) => setSearch(event.target.value)} /></label></div>
        {loading ? <PageLoader /> : filtered.length ? <div className="data-table-wrap"><table className="data-table"><thead><tr><th>Admin</th><th>Role</th><th>Status</th><th>Created</th><th className="actions-column">Actions</th></tr></thead><tbody>
          {filtered.map((admin) => {
            const superAdmin = admin.role === 'superAdmin';
            const ownAccount = (admin.uid || admin.id) === user.uid;
            return <tr key={admin.id}><td><div className="admin-cell"><span className="avatar">{admin.name?.charAt(0) || 'A'}</span><div><strong>{admin.name}</strong><span>{admin.email}</span></div></div></td><td><span className={`role-badge ${superAdmin ? 'super' : ''}`}>{superAdmin ? <ShieldCheck size={15} /> : <UserCog size={15} />}{superAdmin ? 'Super Admin' : 'Admin'}</span></td><td><StatusBadge active={admin.active !== false} /></td><td className="muted-cell">{formatTimestamp(admin.createdAt)}</td><td><div className="row-actions">{isSuperAdmin && !superAdmin ? <><button title="Edit permissions" onClick={() => setEditing(admin)}><Edit3 size={17} /></button><button title="Reset password" onClick={() => setResetting(admin)}><KeyRound size={17} /></button><button className="danger-ghost" title="Remove admin" disabled={ownAccount} onClick={() => setRemoving(admin)}><Trash2 size={17} /></button></> : null}</div></td></tr>;
          })}
        </tbody></table></div> : <EmptyState icon={UserCog} title="No admin users" description="First admin Super Admin bootstrap script thi create karo." />}
      </section>

      <Modal open={creating || Boolean(editing)} title={creating ? 'Create admin' : `Edit ${editing?.name}`} subtitle="Least privilege pramane required access j aapo." onClose={() => { if (!busy) { setCreating(false); setEditing(null); } }}><AdminForm key={editing?.id || 'new'} initial={editing} isNew={creating} busy={busy} onSubmit={saveAdmin} onCancel={() => { setCreating(false); setEditing(null); }} /></Modal>
      <Modal open={Boolean(resetting)} title={`Reset ${resetting?.name} password`} onClose={() => setResetting(null)} size="small"><form className="record-form" onSubmit={resetPassword}><label className="field"><span>New temporary password</span><input type="password" minLength="8" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label><div className="form-actions"><button className="button secondary" type="button" onClick={() => setResetting(null)}>Cancel</button><button className="button primary" disabled={busy}>Update password</button></div></form></Modal>
      <ConfirmDialog open={Boolean(removing)} title="Remove admin account?" description="Firebase Authentication user ane admin profile banne permanently delete thase. Temporary access bandh mate Edit ma Active toggle off karo." confirmLabel="Remove permanently" danger busy={busy} onClose={() => setRemoving(null)} onConfirm={removeAdmin} />
    </div>
  );
}
